import { createReducer } from '@reduxjs/toolkit'
import { SpotOrderStatus } from 'api/v1/spot'
import toastify from 'components/Toastify'
import { uniqBy } from 'lodash'
import { OrderDirection, OrderModeDetails } from 'store/spot/types'
import {
  addClosePositionOrderIds,
  cleanUserPerpDataAction,
  updateCancelAllPerpState,
  updateCancelPerpPendingIds,
  updateCloseAllPerpState,
  updateClosePositionSymbolsAction,
  updateClosingPositionStatusAction,
  updateCurrentPerpSymbol,
  updatePerpBadgeOrderAction,
  updatePerpBadgeTradeAction,
  updatePerpCreateOrderOption,
  updatePerpCurrentOrderTab,
  updatePerpLastInputAmountType,
  updatePerpOrderDirection,
  updatePerpOrderRecordAction,
  updatePerpPositionClearingsAction,
  updatePerpPostOnlyAction,
  updatePerpSelectedOrderMode,
} from './actions'
import { PerpLastInputAmountType, PerpOrderTabType, PerpState } from './types'
import { getPerpClosedOrdersAction } from './actions/getPerpClosedOrdersActions'
import { getPerpPositionClearingsAction } from './actions/getPerpPositionClearingsActions'
import { getPerpFeeRateAction } from './actions/getPerpFeeRateActions'
import { getPerpPositionsAction } from './actions/getPerpPositionsActions'
import { getPerpOpenOrdersAction } from './actions/getPerpOpenOrdersActions'

export const initialState: PerpState = {
  currentPerpSymbol: 0,
  feeRates: undefined,
  openOrders: [],
  badgeOrder: false,
  badgeTrade: false,
  positions: [],
  perpCreateOrderOption: {
    limitPrice: '',
    stopPrice: '',
    baseCurrencyAmount: '',
    quoteCurrencyAmount: '',
  },
  orderMode: OrderModeDetails[0].name,
  perpLastInputAmountType: PerpLastInputAmountType.BaseCurrency,
  perpOrderDirection: OrderDirection.LONG,
  orderTab: PerpOrderTabType.Position,
  cancelPerpPendingIds: [],
  cancelAllPerpState: false,
  closePositionSymbols: [],
  closePositionOrderIds: [],
  closeAllPerpState: false,
  closedOrders: {
    list: [],
    isLastPage: false,
    offsetId: 0,
  },
  positionClearings: {
    list: [],
    isLastPage: false,
    offsetId: 0,
  },
  postOnly: false,
}

export default createReducer<PerpState>(initialState, (builder) => {
  builder
    .addCase(updateCurrentPerpSymbol, (state, { payload }) => {
      state.currentPerpSymbol = payload
    })
    .addCase(getPerpOpenOrdersAction.fulfilled, (state, { payload }) => {
      state.openOrders = payload
    })
    .addCase(updatePerpBadgeOrderAction, (state, { payload }) => {
      state.badgeOrder = payload
    })
    .addCase(updatePerpBadgeTradeAction, (state, { payload }) => {
      state.badgeTrade = payload
    })
    .addCase(updatePerpOrderRecordAction, (state, { payload }) => {
      // Update user order records
      switch (payload.type) {
        case 'order_pending':
          state.openOrders = uniqBy([payload.data, ...state.openOrders], 'id')
          break

        case 'order_filled':
          // FULLY_FILLED: remove record from open orders
          const isFullyFilled =
            payload.data.status === SpotOrderStatus.FULLY_FILLED
          if (isFullyFilled) {
            state.openOrders = state.openOrders.filter(
              (v) => v.id !== payload.data.id,
            )

            // Add records to closed orders
            state.closedOrders.list = uniqBy(
              [payload.data, ...state.closedOrders.list],
              'id',
            )
          }
          // PARTIAL_FILLED: update record in  open order
          else {
            const index = state.openOrders.findIndex(
              (v) => v.id === payload.data.id,
            )
            if (index > -1) {
              state.openOrders[index] = payload.data
            }
          }
          break

        case 'order_cancelled':
        case 'order_failed':
          const orders = state.openOrders.filter(
            (v) => v.id !== payload.data.id,
          )
          state.openOrders = orders

          // Add records to closed orders
          state.closedOrders.list = uniqBy(
            [payload.data, ...state.closedOrders.list],
            'id',
          )
          break
      }
    })
    .addCase(updateClosingPositionStatusAction, (state, { payload }) => {
      // Handling the pushed data for close position orders,
      // update the 'closePositionOrderIds' and 'closePositionSymbols')
      if (state.closePositionOrderIds.includes(Number(payload.data.id))) {
        switch (payload.data.status) {
          case SpotOrderStatus.FULLY_FILLED:
            toastify.success('Close position successful.')
            break
          case SpotOrderStatus.PARTIAL_CANCELLED:
          case SpotOrderStatus.PARTIAL_FILLED:
            toastify.warn(
              'Close position partially filled, remaining order cancelled.',
            )
            break

          case SpotOrderStatus.FULLY_CANCELLED:
            toastify.error(
              'Close position order was not executed and has been cancelled.',
            )
            break

          case SpotOrderStatus.FAILED:
            toastify.error('Close position failed.')
            break
        }

        // Drop the id in closing position orders list
        state.closePositionOrderIds = state.closePositionOrderIds.filter(
          (v) => Number(v) !== Number(payload.data.id),
        )

        // Stop the position closing status
        if (state.closePositionSymbols.includes(String(payload.data.symbol))) {
          state.closePositionSymbols = state.closePositionSymbols.filter(
            (v) => v !== String(payload.data.symbol),
          )
        }
      }
    })
    .addCase(getPerpPositionsAction.fulfilled, (state, { payload }) => {
      state.positions = payload
    })
    .addCase(getPerpFeeRateAction.fulfilled, (state, { payload }) => {
      state.feeRates = payload
    })
    .addCase(updatePerpCreateOrderOption, (state, { payload }) => {
      state.perpCreateOrderOption[payload.option] = payload.value
    })
    .addCase(updateClosePositionSymbolsAction, (state, { payload }) => {
      state.closePositionSymbols = payload
    })
    .addCase(addClosePositionOrderIds, (state, { payload }) => {
      state.closePositionOrderIds = [...state.closePositionOrderIds, ...payload]
    })
    .addCase(updatePerpSelectedOrderMode, (state, { payload }) => {
      state.orderMode = payload
    })
    .addCase(updatePerpLastInputAmountType, (state, { payload }) => {
      state.perpLastInputAmountType = payload
    })
    .addCase(updatePerpOrderDirection, (state, { payload }) => {
      state.perpOrderDirection = payload
    })
    .addCase(updatePerpCurrentOrderTab, (state, { payload }) => {
      state.orderTab = payload
    })
    .addCase(getPerpClosedOrdersAction.fulfilled, (state, { payload }) => {
      const { list, isLastPage, offsetId } = payload
      state.closedOrders.list = list
      state.closedOrders.isLastPage = isLastPage
      state.closedOrders.offsetId = offsetId
    })
    .addCase(getPerpPositionClearingsAction.fulfilled, (state, { payload }) => {
      const { list, isLastPage, offsetId } = payload
      state.positionClearings.list = list
      state.positionClearings.isLastPage = isLastPage
      state.positionClearings.offsetId = offsetId
    })
    .addCase(updatePerpPositionClearingsAction, (state, { payload }) => {
      state.positionClearings.list = uniqBy(
        [payload.data, ...state.positionClearings.list],
        'txHash',
      )
    })
    .addCase(updateCancelAllPerpState, (state, { payload }) => {
      state.cancelAllPerpState = payload
    })
    .addCase(updateCloseAllPerpState, (state, { payload }) => {
      state.closeAllPerpState = payload
    })
    .addCase(updateCancelPerpPendingIds, (state, { payload }) => {
      state.cancelPerpPendingIds = payload
    })
    .addCase(updatePerpPostOnlyAction, (state, { payload }) => {
      state.postOnly = payload
    })
    .addCase(cleanUserPerpDataAction, (state) => {
      state.openOrders = []
      state.badgeOrder = false
      state.badgeTrade = false
      state.positions = []
      state.cancelPerpPendingIds = []
      state.cancelAllPerpState = false
      state.closePositionSymbols = []
      state.closePositionOrderIds = []
      state.closeAllPerpState = false
      state.closedOrders = {
        list: [],
        isLastPage: false,
        offsetId: 0,
      }
      state.positionClearings = {
        list: [],
        isLastPage: false,
        offsetId: 0,
      }
    })
})
